import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { format } from 'date-fns';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';

import Icon from '../../components/AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import { progressPhotoService } from '../../services/supabaseService';
import ProgressPhotoCard from './components/ProgressPhotoCard';
import UploadPhotoModal from './components/UploadPhotoModal';
import BeforeAfterComparison from './components/BeforeAfterComparison';
import ProgressNotesPanel from './components/ProgressNotesPanel';

const ProgressTracking = () => {
  const { user, userProfile } = useAuth();
  const [activeTab, setActiveTab] = useState('gallery');
  const [progressPhotos, setProgressPhotos] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [selectedPhotos, setSelectedPhotos] = useState({ before: null, after: null });
  const [viewMode, setViewMode] = useState('grid'); // grid or timeline
  const [filterCategory, setFilterCategory] = useState('all'); // all, front, side, back

  const isClient = userProfile?.role === 'client';

  useEffect(() => {
    loadProgressPhotos();
  }, [user]);

  const loadProgressPhotos = async () => {
    setIsLoading(true);
    try {
      const { data } = await progressPhotoService?.getProgressPhotos(user?.id);
      setProgressPhotos(data || []);
    } catch (error) {
      console.error('Error loading progress photos:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePhotoUpload = async (file, metadata) => {
    const { data, error } = await progressPhotoService?.uploadPhoto(file, metadata);
    if (!error && data) {
      setProgressPhotos(prev => [data, ...prev]);
      setIsUploadModalOpen(false);
    }
  };

  const handleDeletePhoto = async (photoId, photoUrl) => {
    const { error } = await progressPhotoService?.deletePhoto(photoId, photoUrl);
    if (!error) {
      setProgressPhotos(prev => prev?.filter(p => p?.id !== photoId));
    }
  };

  // Generate body metrics timeline data from photos
  const metricsTimelineData = progressPhotos
    ?.filter(p => p?.weight || p?.bodyFatPercentage)
    ?.sort((a, b) => new Date(a?.takenAt) - new Date(b?.takenAt))
    ?.map(photo => ({
      date: format(new Date(photo?.takenAt), 'MMM dd'),
      weight: photo?.weight || null,
      bodyFat: photo?.bodyFatPercentage || null
    }));

  // Calculate progress statistics
  const calculateProgressStats = () => {
    if (progressPhotos?.length < 2) return null;
    
    const sortedPhotos = [...progressPhotos]?.sort((a, b) => new Date(a?.takenAt) - new Date(b?.takenAt));
    const firstPhoto = sortedPhotos?.[0];
    const latestPhoto = sortedPhotos?.[sortedPhotos?.length - 1];

    const weightChange = latestPhoto?.weight && firstPhoto?.weight 
      ? (latestPhoto?.weight - firstPhoto?.weight)?.toFixed(1)
      : null;
    
    const bodyFatChange = latestPhoto?.bodyFatPercentage && firstPhoto?.bodyFatPercentage
      ? (latestPhoto?.bodyFatPercentage - firstPhoto?.bodyFatPercentage)?.toFixed(1)
      : null;

    return { weightChange, bodyFatChange, totalPhotos: progressPhotos?.length };
  };

  const progressStats = calculateProgressStats();

  const filteredPhotos = filterCategory === 'all' 
    ? progressPhotos 
    : progressPhotos?.filter(p => p?.category === filterCategory);

  return (
    <>
      <Helmet>
        <title>Progress Tracking - FormForge</title>
      </Helmet>
      <div className="min-h-screen" style={{ background: '#0A0A0B' }}>
        <Header />
        <main className="container mx-auto px-4 py-8">
          {/* Header Section */}
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-8 gap-4">
            <div>
              <h1 className="text-3xl font-bold" style={{ fontFamily: "'Inter Tight', sans-serif", color: '#E8EAED' }}>Progress Tracking</h1>
              <p className="mt-2" style={{ color: '#6B7280' }}>Document your transformation journey with photos, metrics, and notes</p>
            </div>
            {isClient && (
              <Button onClick={() => setIsUploadModalOpen(true)} size="lg">
                <Icon name="Camera" size={20} className="mr-2" />
                Upload Photo
              </Button>
            )}
          </div>

          {/* Progress Statistics Cards */}
          {progressStats && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="rounded-xl p-6" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', backdropFilter: 'blur(20px)' }}>
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ background: 'rgba(0,212,255,0.1)' }}>
                    <Icon name="Camera" size={24} style={{ color: '#00D4FF' }} />
                  </div>
                  <div>
                    <p className="text-sm" style={{ color: '#6B7280' }}>Total Photos</p>
                    <p className="text-2xl font-bold" style={{ fontFamily: "'Inter Tight', sans-serif", color: '#00D4FF' }}>{progressStats?.totalPhotos}</p>
                  </div>
                </div>
              </div>

              {progressStats?.weightChange && (
                <div className="rounded-xl p-6" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', backdropFilter: 'blur(20px)' }}>
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ background: 'rgba(170,255,0,0.1)' }}>
                      <Icon name="TrendingDown" size={24} style={{ color: '#AAFF00' }} />
                    </div>
                    <div>
                      <p className="text-sm" style={{ color: '#6B7280' }}>Weight Change</p>
                      <p className="text-2xl font-bold" style={{ fontFamily: "'Inter Tight', sans-serif", color: '#AAFF00' }}>
                        {progressStats?.weightChange > 0 ? '+' : ''}{progressStats?.weightChange} lbs
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {progressStats?.bodyFatChange && (
                <div className="rounded-xl p-6" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', backdropFilter: 'blur(20px)' }}>
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ background: 'rgba(255,107,0,0.1)' }}>
                      <Icon name="Percent" size={24} style={{ color: '#FF6B00' }} />
                    </div>
                    <div>
                      <p className="text-sm" style={{ color: '#6B7280' }}>Body Fat Change</p>
                      <p className="text-2xl font-bold" style={{ fontFamily: "'Inter Tight', sans-serif", color: '#FF6B00' }}>
                        {progressStats?.bodyFatChange > 0 ? '+' : ''}{progressStats?.bodyFatChange}%
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Tab Navigation */}
          <div className="flex flex-wrap gap-3 mb-6">
            {[
              { id: 'gallery', icon: 'Grid', label: 'Photo Gallery' },
              { id: 'comparison', icon: 'ArrowLeftRight', label: 'Before/After' },
              { id: 'metrics', icon: 'TrendingUp', label: 'Body Metrics' },
              { id: 'notes', icon: 'FileText', label: 'Progress Notes' }
            ]?.map(tab => (
              <button key={tab?.id} onClick={() => setActiveTab(tab?.id)} className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all" style={activeTab === tab?.id ? { background: 'rgba(255,107,0,0.15)', color: '#FF6B00', border: '1px solid rgba(255,107,0,0.3)' } : { background: 'rgba(255,255,255,0.04)', color: '#6B7280', border: '1px solid rgba(255,255,255,0.08)' }}>
                <Icon name={tab?.icon} size={16} />
                {tab?.label}
              </button>
            ))}
          </div>

          {isLoading ? (
            <div className="flex justify-center items-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: '#FF6B00' }}></div>
            </div>
          ) : (
            <>
              {activeTab === 'gallery' && (
                <div>
                  <div className="flex flex-wrap justify-between items-center mb-6 gap-4">
                    <div className="flex gap-2">
                      {[{ id: 'grid', icon: 'Grid', label: 'Grid' }, { id: 'timeline', icon: 'Calendar', label: 'Timeline' }]?.map(v => (
                        <button key={v?.id} onClick={() => setViewMode(v?.id)} className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-all" style={viewMode === v?.id ? { background: 'rgba(255,107,0,0.15)', color: '#FF6B00', border: '1px solid rgba(255,107,0,0.3)' } : { background: 'rgba(255,255,255,0.04)', color: '#6B7280', border: '1px solid rgba(255,255,255,0.08)' }}>
                          <Icon name={v?.icon} size={14} />{v?.label}
                        </button>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      {['all', 'front', 'side', 'back']?.map(cat => (
                        <button key={cat} onClick={() => setFilterCategory(cat)} className="px-3 py-1.5 rounded-lg text-sm capitalize transition-all" style={filterCategory === cat ? { background: 'rgba(0,212,255,0.15)', color: '#00D4FF', border: '1px solid rgba(0,212,255,0.3)' } : { background: 'rgba(255,255,255,0.04)', color: '#6B7280', border: '1px solid rgba(255,255,255,0.08)' }}>
                          {cat}
                        </button>
                      ))}
                    </div>
                  </div>

                  {filteredPhotos?.length === 0 ? (
                    <div className="text-center py-20 rounded-2xl" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
                      <Icon name="Camera" size={48} style={{ color: '#6B7280' }} className="mx-auto mb-4" />
                      <h3 className="text-lg font-semibold mb-2" style={{ color: '#E8EAED' }}>No photos yet</h3>
                      <p style={{ color: '#6B7280' }}>Upload your first progress photo to start tracking your transformation</p>
                    </div>
                  ) : (
                    <div className={viewMode === 'grid' ? 'grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4' : 'space-y-4'}>
                      {filteredPhotos?.map(photo => (
                        <ProgressPhotoCard key={photo?.id} photo={photo} onDelete={handleDeletePhoto} viewMode={viewMode} isClient={isClient} />
                      ))}
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'comparison' && (
                <BeforeAfterComparison photos={progressPhotos} selectedPhotos={selectedPhotos} onSelectPhoto={(type, photo) => setSelectedPhotos(prev => ({ ...prev, [type]: photo }))} />
              )}

              {activeTab === 'metrics' && (
                <div className="rounded-2xl p-6" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
                  <h3 className="text-lg font-semibold mb-6" style={{ color: '#E8EAED' }}>Body Metrics Timeline</h3>
                  {metricsTimelineData?.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={metricsTimelineData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                        <XAxis dataKey="date" stroke="#6B7280" tick={{ fill: '#6B7280', fontSize: 12 }} />
                        <YAxis stroke="#6B7280" tick={{ fill: '#6B7280', fontSize: 12 }} />
                        <Tooltip contentStyle={{ background: 'rgba(10,10,11,0.95)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', color: '#E8EAED' }} />
                        {metricsTimelineData?.some(d => d?.weight) && <Line type="monotone" dataKey="weight" stroke="#FF6B00" strokeWidth={2} dot={{ fill: '#FF6B00', r: 4 }} name="Weight (lbs)" />}
                        {metricsTimelineData?.some(d => d?.bodyFat) && <Line type="monotone" dataKey="bodyFat" stroke="#00D4FF" strokeWidth={2} dot={{ fill: '#00D4FF', r: 4 }} name="Body Fat %" />}
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="text-center py-12" style={{ color: '#6B7280' }}>Add weight or body fat data to your photos to see metrics here</div>
                  )}
                </div>
              )}

              {activeTab === 'notes' && (
                <ProgressNotesPanel userId={user?.id} isClient={isClient} />
              )}
            </>
          )}
        </main>

        {isUploadModalOpen && (
          <UploadPhotoModal onClose={() => setIsUploadModalOpen(false)} onUpload={handlePhotoUpload} />
        )}
      </div>
    </>
  );
};

export default ProgressTracking;